import React, { useEffect, useState, useCallback } from "react";
import {
  Col,
  Row,
  Space,
  notification,
  Typography,
  Modal,
  Select,
  Input,
} from "antd";
import { usePermissions } from "../../../PermissionsProvider";
import Card from "antd/es/card/Card";
import { PlusOutlined } from "@ant-design/icons";
import { PrimaryButton } from "../../shared/button/Buttons";
import { ValidationService } from "../../../services/ValidationService";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { ConfigService } from "../../../services/ConfigService";
import { FinetuneRunNew, ValidationRun } from "../../../model_gen/finetuning";
import ValidationRunTable from "./ValidationRunTable";

const ValidationOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const [validationRuns, setValidationRuns] = useState<ValidationRun[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRuns, setTotalRuns] = useState(0);
  const pageSize = 10;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFinetuneRun, setSelectedFinetuneRun] = useState<number | null>(
    null
  );
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [finetuneRuns, setFinetuneRuns] = useState<FinetuneRunNew[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [description, setDescription] = useState<string>("");

  const refreshValidationRuns = useCallback(
    (showLoading = true) => {
      const loadingHandler = showLoading ? setIsLoading : null;
      ValidationService.getValidationRuns(loadingHandler, apiKey).subscribe(
        (response: ValidationRun[]) => {
          setValidationRuns(response);
          setTotalRuns(response.length);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to load validation runs",
          });
        }
      );
    },
    [apiKey]
  );

  useEffect(() => {
    refreshValidationRuns(true);

    // Set up auto-refresh interval (15 seconds)
    const intervalId = setInterval(() => {
      refreshValidationRuns(false); // Don't show loading indicator for background polling
    }, 15000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [refreshValidationRuns, currentPage]);

  useEffect(() => {
    // Fetch finetune runs for the modal
    FinetuneRunService.getFinetuneRuns(0, 100, null, apiKey).subscribe(
      (response: { items: FinetuneRunNew[]; total: number }) => {
        setFinetuneRuns(response.items);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to load finetune runs",
        });
      }
    );

    // Fetch models
    ConfigService.getModels(-1, null, apiKey).subscribe(
      (modelData: any) => {
        setModels(modelData.all_models);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
      }
    );
  }, [apiKey]);

  const handleNewValidationRun = () => {
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedFinetuneRun(null);
    setSelectedModel("");
    setDescription("");
  };

  const handleModalOk = () => {
    if (!selectedFinetuneRun || !selectedModel) {
      notification.error({
        message: "Error",
        description: "Please select both a finetune run and a model",
      });
      return;
    }

    setIsSubmitting(true);
    const finetuneRunId = selectedFinetuneRun.toString();
    const payload = { model: selectedModel, description: description };

    ValidationService.runValidation(
      finetuneRunId,
      payload,
      (loading: boolean) => {
        setIsSubmitting(loading);
      },
      apiKey
    ).subscribe(
      () => {
        setIsModalOpen(false);
        notification.success({
          message: "Success",
          description: "Validation run created successfully",
        });
        refreshValidationRuns(true);
        setDescription("");
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to create validation run",
        });
        setIsSubmitting(false);
      }
    );
  };

  return (
    <Card className="validation-runs-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}>
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              Validation Runs
            </Typography>
          </Col>
          <Col>
            <PrimaryButton
              title="New Validation Run"
              className="buttonWrapper"
              icon={<PlusOutlined />}
              onClick={handleNewValidationRun}
            />
          </Col>
        </Row>
        <ValidationRunTable
          validationRuns={validationRuns}
          isLoading={isLoading}
          showCompany={true}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRuns,
            onChange: (page) => setCurrentPage(page),
          }}
        />
      </Space>

      <Modal
        title="Create New Validation Run"
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okButtonProps={{
          disabled: !selectedFinetuneRun || !selectedModel || isSubmitting,
          loading: isSubmitting,
        }}
        cancelButtonProps={{
          disabled: isSubmitting,
        }}>
        <Space
          direction="vertical"
          style={{ width: "100%", marginTop: "16px" }}>
          <div>
            <Typography.Text strong>Finetune Run</Typography.Text>
            <Select
              style={{ width: "100%", marginTop: "8px" }}
              placeholder="Select a finetune run"
              value={selectedFinetuneRun}
              onChange={(value) => setSelectedFinetuneRun(value)}
              options={finetuneRuns.map((run) => ({
                value: run.id,
                label: run.display_name,
              }))}
              disabled={isSubmitting}
            />
          </div>
          <div style={{ marginTop: "16px" }}>
            <Typography.Text strong>Model</Typography.Text>
            <Select
              style={{ width: "100%", marginTop: "8px" }}
              placeholder="Select a model"
              value={selectedModel}
              onChange={(value) => setSelectedModel(value)}
              options={models.map((model) => ({
                value: model,
                label: model,
              }))}
              disabled={isSubmitting}
            />
          </div>
          <div style={{ marginTop: "16px" }}>
            <Typography.Text strong>Description</Typography.Text>
            <Input
              style={{ width: "100%", marginTop: "8px" }}
              placeholder="Enter a description for this validation run"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </Space>
      </Modal>
    </Card>
  );
};

export default ValidationOverview;
